/**
 * AI 任务历史上报（业务服务器接口，与 AI 接口分离）
 * POST /web/v1/saveOrUpdateAIHistory
 */
import apiClient from './config'
import { HISTORY_SOURCE_POLLO } from '@/config/ai-history-source'

// 终态去重：同一任务（historyId/taskId）只允许一次 state=2/3 上报成功
var FINAL_STATE_TRACKER = Object.create(null)

function getFinalKey(params) {
  if (params && params.historyId != null && params.historyId !== '') {
    return 'h:' + String(params.historyId)
  }
  if (params && params.taskId) {
    return 't:' + String(params.taskId)
  }
  return ''
}

function formatCreateDate(d) {
  var y = d.getFullYear()
  var m = String(d.getMonth() + 1).padStart(2, '0')
  var day = String(d.getDate()).padStart(2, '0')
  var h = String(d.getHours()).padStart(2, '0')
  var min = String(d.getMinutes()).padStart(2, '0')
  var s = String(d.getSeconds()).padStart(2, '0')
  return y + '-' + m + '-' + day + ' ' + h + ':' + min + ':' + s
}

/**
 * 保存或更新 AI 历史记录
 * @param {Object} params
 * @param {string} params.aiName - 模型名称
 * @param {string|number} [params.historyId] - 历史记录 ID（首次上报返回；后续更新需传）
 * @param {string} params.taskId - 任务 ID
 * @param {string} params.aiDesc - 提示词（仅正向）
 * @param {number} params.state - 1 生成中 2 成功 3 失败
 * @param {string} [params.aiResultUrl=''] - 结果 URL，轮询完成后填写
 * @param {string} params.createDate - 提交任务时间，格式 'YYYY-MM-DD HH:mm:ss'
 * @param {number} [params.credits] - 本次消耗积分（轮询成功时传）
 * @param {string} [params.image_large_url=''] - 音乐封面大图 URL（逗号分隔）
 * @param {string} [params.message=''] - 失败原因详情（state=3 时建议传）
 * @param {number} [params.aiSource] - 渠道来源（见 @/config/ai-history-source）；未传时默认 101（Pollo）并 console.warn
 * @returns {Promise}
 */
export function saveOrUpdateAIHistory(params) {
  var miToken = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null
  if (typeof console !== 'undefined' && console.log) {
    console.log('[saveOrUpdateAIHistory] 被调用', { aiName: params.aiName, hasToken: !!miToken })
  }
  if (!miToken) return Promise.resolve()
  var isFinalState = params && (params.state === 2 || params.state === 3)
  var finalKey = getFinalKey(params)
  if (isFinalState && finalKey && FINAL_STATE_TRACKER[finalKey]) {
    console.log('[saveOrUpdateAIHistory] 终态重复上报已跳过', {
      key: finalKey,
      currentState: params.state,
      recordedState: FINAL_STATE_TRACKER[finalKey]
    })
    return Promise.resolve({ skipped: true })
  }
  var body = {
    miToken: miToken,
    aiName: params.aiName || '',
    taskId: params.taskId || '',
    aiDesc: params.aiDesc || '',
    state: params.state,
    aiResultUrl: params.aiResultUrl != null ? params.aiResultUrl : '',
    image_large_url: params.image_large_url != null ? params.image_large_url : '',
    message: params.message != null ? String(params.message) : '',
    createDate: params.createDate || formatCreateDate(new Date())
  }
  if (params.historyId != null && params.historyId !== '') {
    body.historyId = params.historyId
  }
  if (params.credits != null) {
    body.credits = params.credits
  }
  if (params.aiMediaType != null && params.aiMediaType !== '') {
    body.aiMediaType = params.aiMediaType
  }
  var sourceVal = HISTORY_SOURCE_POLLO
  if (params.aiSource != null && params.aiSource !== '') {
    var n = Number(params.aiSource)
    sourceVal = Number.isNaN(n) ? HISTORY_SOURCE_POLLO : n
    if (Number.isNaN(n) && typeof console !== 'undefined' && console.warn) {
      console.warn('[saveOrUpdateAIHistory] aiSource 非法，已默认', HISTORY_SOURCE_POLLO, params.aiName)
    }
  } else if (typeof console !== 'undefined' && console.warn) {
    console.warn('[saveOrUpdateAIHistory] aiSource 未传，已默认', HISTORY_SOURCE_POLLO, params.aiName)
  }
  body.aiSource = sourceVal
  console.log('[saveOrUpdateAIHistory] 请求', body)
  return apiClient.post('/saveOrUpdateAIHistory', body).then(function(res) {
    console.log('[saveOrUpdateAIHistory] 响应', res)
    if (isFinalState && finalKey) {
      FINAL_STATE_TRACKER[finalKey] = params.state
    }
    return res
  }).catch(function(err) {
    console.log('[saveOrUpdateAIHistory] 响应异常', err && err.message, err)
    return Promise.reject(err)
  })
}

/**
 * 获取用户 AI 历史记录列表
 * @param {Object} params
 * @param {number} [params.pageNumber=0] - 页码（从0开始）
 * @param {number} [params.pageSize=20] - 每页数量
 * @param {number} [params.state=0] - 状态筛选：0 全部 1 pending 2 finish
 * @returns {Promise}
 */
export function getAIHistory(params) {
  var miToken = localStorage.getItem('token')
  if (!miToken) {
    return Promise.reject(new Error('No token'))
  }
  var body = {
    miToken: miToken,
    state: params && params.state !== undefined ? params.state : 0,
    pageNumber: params && params.pageNumber !== undefined ? params.pageNumber : 0,
    pageSize: params && params.pageSize ? params.pageSize : 20
  }
  return apiClient.post('/historyList', body)
}

export { formatCreateDate }
